import { useRef, type ChangeEvent } from 'react'

type Props = {
  portraitUrl: string
  landscapeUrl: string
  uploadingPortrait?: boolean
  uploadingLandscape?: boolean
  onUploadPortrait: (file: File) => Promise<void> | void
  onUploadLandscape: (file: File) => Promise<void> | void
  onPortraitUrlChange: (value: string) => void
  onLandscapeUrlChange: (value: string) => void
  onClearPortrait: () => void
  onClearLandscape: () => void
}

export default function BusinessPixeImageEditor({
  portraitUrl,
  landscapeUrl,
  uploadingPortrait = false,
  uploadingLandscape = false,
  onUploadPortrait,
  onUploadLandscape,
  onPortraitUrlChange,
  onLandscapeUrlChange,
  onClearPortrait,
  onClearLandscape,
}: Props) {
  const portraitInputRef = useRef<HTMLInputElement | null>(null)
  const landscapeInputRef = useRef<HTMLInputElement | null>(null)

  function onPortraitFileChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (file) onUploadPortrait(file)
    if (e.target) e.target.value = ''
  }

  function onLandscapeFileChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (file) onUploadLandscape(file)
    if (e.target) e.target.value = ''
  }

  return (
    <div className="rounded-xl border border-white/10 p-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="font-semibold">Pixe image</div>
          <div className="text-xs text-white/70">Use a portrait (3:4) plus an optional landscape crop (16:9) for your header.</div>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-1 items-start gap-3 md:grid-cols-5">
        <div className="md:col-span-2">
          <div className="mb-1 flex h-7 items-center justify-between">
            <div className="whitespace-nowrap text-xs text-white/60">Portrait (3:4)</div>
            {portraitUrl ? (
              <button type="button" className="rounded border border-white/15 px-2 py-1 text-xs text-white/80 hover:bg-white/10" onClick={onClearPortrait}>
                Remove
              </button>
            ) : null}
          </div>
          {portraitUrl ? (
            <img src={portraitUrl} alt="Portrait" className="h-40 w-full rounded-lg border border-white/10 object-cover" />
          ) : (
            <div className="flex h-40 w-full items-center justify-center rounded-lg border border-dashed border-white/15 bg-white/5 text-xs text-white/50">
              Paste a portrait image URL
            </div>
          )}
          <div className="mt-2 flex items-center gap-2">
            <input ref={portraitInputRef} type="file" accept="image/*" className="hidden" onChange={onPortraitFileChange} />
            <button
              type="button"
              onClick={() => portraitInputRef.current?.click()}
              disabled={uploadingPortrait}
              className="inline-flex items-center rounded-lg border border-white/15 bg-white/5 px-3 py-1.5 text-xs hover:bg-white/10 disabled:opacity-60"
            >
              {uploadingPortrait ? 'Uploading...' : 'Upload image'}
            </button>
            <span className="text-xs text-white/40">or paste URL</span>
          </div>
          <input
            value={portraitUrl}
            onChange={(e) => onPortraitUrlChange(e.target.value)}
            placeholder="https:// portrait URL"
            className="mt-2 w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand"
          />
        </div>

        <div className="md:col-span-3">
          <div className="mb-1 flex h-7 items-center justify-between">
            <div className="whitespace-nowrap text-xs text-white/60">Landscape (16:9)</div>
            {landscapeUrl ? (
              <button type="button" className="rounded border border-white/15 px-2 py-1 text-xs text-white/80 hover:bg-white/10" onClick={onClearLandscape}>
                Clear
              </button>
            ) : null}
          </div>
          {landscapeUrl ? (
            <img src={landscapeUrl} alt="Landscape" className="h-40 w-full rounded-lg border border-white/10 object-cover" />
          ) : (
            <div className="flex h-40 w-full items-center justify-center rounded-lg border border-dashed border-white/15 bg-white/5 text-xs text-white/50">
              Optional landscape variation
            </div>
          )}
          <div className="mt-2 flex items-center gap-2">
            <input ref={landscapeInputRef} type="file" accept="image/*" className="hidden" onChange={onLandscapeFileChange} />
            <button
              type="button"
              onClick={() => landscapeInputRef.current?.click()}
              disabled={uploadingLandscape}
              className="inline-flex items-center rounded-lg border border-white/15 bg-white/5 px-3 py-1.5 text-xs hover:bg-white/10 disabled:opacity-60"
            >
              {uploadingLandscape ? 'Uploading...' : 'Upload image'}
            </button>
            <span className="text-xs text-white/40">or paste URL</span>
          </div>
          <input
            value={landscapeUrl}
            onChange={(e) => onLandscapeUrlChange(e.target.value)}
            placeholder="https:// landscape URL"
            className="mt-2 w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand"
          />
        </div>
      </div>
    </div>
  )
}
